import { useState, useRef, useEffect } from 'react';
import { StarIcon, ExternalLinkIcon } from "./icons";
import { searchHotels } from "../../services/api";
import * as Sentry from "@sentry/react";
import { SearchError } from "../../errors/sentryErrors";

const formatDateForBooking = (dateStr) => {
  if (!dateStr || dateStr === '-') return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const getAccommodationTypes = (accommodation) => {
  if (!accommodation || accommodation.length === 0) return ['hotels'];

  const types = [];
  const accStr = accommodation.map(a => a.toLowerCase()).join(' ');

  if (accStr.includes('star') || accStr.includes('3star') || accStr.includes('4star') || accStr.includes('5star')) {
    types.push('hotels');
  }
  if (accStr.includes('airbnb')) {
    types.push('airbnb');
  }
  if (accStr.includes('homestay')) {
    types.push('homestay');
  }
  if (accStr.includes('hostel')) {
    types.push('hostel');
  }

  return types.length > 0 ? types : ['hotels'];
};

const getSectionTitle = (accommodationTypes) => {
  if (accommodationTypes.length === 1) {
    switch (accommodationTypes[0]) {
      case 'hotels': return 'Hotel Recommendations';
      case 'airbnb': return 'Airbnb Recommendations';
      case 'homestay': return 'Homestay Recommendations';
      case 'hostel': return 'Hostel Recommendations';
      default: return 'Accommodation Recommendations';
    }
  }
  return 'Stay Recommendations';
};

const AccommodationBookingDropdown = ({ hotel, checkIn, checkOut, guests, starRating, tripData, accommodationType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const normalizedAccommodationType = (() => {
    const type = accommodationType?.toLowerCase() || 'hotels';
    if (type === 'hotel') return 'hotels';
    return type;
  })();

  const getGuestInfo = () => {
    if (tripData) {
      return {
        adults: tripData.adults || 1,
        children: tripData.children || 0,
        infants: tripData.infants || 0,
        rooms: Math.max(1, Math.ceil((tripData.adults || 1) / 2))
      };
    }
    if (!guests) return { adults: 1, children: 0, infants: 0, rooms: 1 };
    const adultMatch = guests.match(/(\d+)\s*Adult/i);
    const childMatch = guests.match(/(\d+)\s*Child/i);
    const adults = adultMatch ? parseInt(adultMatch[1]) : 1;
    const children = childMatch ? parseInt(childMatch[1]) : 0;
    const rooms = Math.max(1, Math.ceil(adults / 2));
    return { adults, children, infants: 0, rooms };
  };

  const { adults, children, rooms } = getGuestInfo();

  const checkInYYYYMMDD = formatDateForBooking(checkIn);
  const checkOutYYYYMMDD = formatDateForBooking(checkOut);

  const formatForMMT = (dateStr) => {
    if (!dateStr || dateStr === '-') return '';
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [y, m, d] = dateStr.split('-');
      return `${m}${d}${y}`;
    }
    return dateStr.replace(/-/g, '');
  };

  const checkInMMT = formatForMMT(checkInYYYYMMDD);
  const checkOutMMT = formatForMMT(checkOutYYYYMMDD);

  const formatForGoibibo = (dateStr) => {
    if (!dateStr || dateStr === '-') return '';
    return dateStr.replace(/-/g, '');
  };

  const checkInGoibibo = formatForGoibibo(checkInYYYYMMDD);
  const checkOutGoibibo = formatForGoibibo(checkOutYYYYMMDD);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 350;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top, left;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = Math.max(8, rect.top - dropdownHeight - 4);
      } else {
        top = rect.bottom + 4;
      }

      left = Math.min(
        Math.max(8, rect.right - 260),
        viewportWidth - 268
      );

      setDropdownPosition({ top, left });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const hotelName = hotel.name || hotel.hotelName || '';
  const hotelLocation = hotel.address || hotel.location || '';
  const destination = tripData?.destination || hotelLocation || '';
  const cityName = destination.split(',')[0].trim().toLowerCase().replace(/\s+/g, '-');
  const encodedHotel = encodeURIComponent(hotelName);
  const encodedCity = encodeURIComponent(destination.split(',')[0].trim());
  const encodedSearch = encodeURIComponent(`${hotelName} ${hotelLocation}`.trim());

  const getLOS = () => {
    try {
      const start = new Date(checkInYYYYMMDD);
      const end = new Date(checkOutYYYYMMDD);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(1, diffDays);
    } catch {
      return 1;
    }
  };

  const getBookingOptions = () => {
    const baseOptions = [
      {
        label: 'Search on Google',
        url: `https://www.google.com/search?q=${encodedHotel}+${accommodationType === 'hotels' ? 'hotel' : accommodationType}+${encodedCity}+booking`,
        icon: '🔍',
        types: ['hotels', 'airbnb', 'homestay', 'hostel']
      }
    ];

    const hotelOptions = [
      {
        label: 'Booking.com',
        url: hotel.bookingUrl || `https://www.booking.com/searchresults.html?ss=${encodedSearch}&checkin=${checkInYYYYMMDD}&checkout=${checkOutYYYYMMDD}&group_adults=${adults}&group_children=${children}&no_rooms=${rooms}&selected_currency=INR`,
        icon: '🏨',
        types: ['hotels', 'hostel']
      },
      {
        label: 'Agoda',
        url: `https://www.agoda.com/search?checkIn=${checkInYYYYMMDD}&checkOut=${checkOutYYYYMMDD}&rooms=${rooms}&adults=${adults}&children=${children}&los=${getLOS()}&textToSearch=${encodeURIComponent(destination)}&priceCur=INR`,
        icon: '🌏',
        types: ['hotels']
      },
      {
        label: 'MakeMyTrip',
        url: `https://www.makemytrip.com/hotels/hotel-listing/?checkin=${checkInMMT}&checkout=${checkOutMMT}&roomStayQualifier=${rooms}e${adults}e${children}&locusType=city&searchText=${encodeURIComponent(destination.split(',')[0].trim())}&country=IN`,
        icon: '🎫',
        types: ['hotels']
      },
      {
        label: 'Goibibo',
        url: `https://www.goibibo.com/hotels/hotels-in-${cityName}/?checkin=${checkInGoibibo}&checkout=${checkOutGoibibo}&adults=${adults}&children=${children}&rooms=${rooms}`,
        icon: '🏪',
        types: ['hotels']
      }
    ];

    const airbnbOptions = [
      {
        label: 'Airbnb',
        url: `https://www.airbnb.com/s/${encodeURIComponent(destination)}/homes?checkin=${checkInYYYYMMDD}&checkout=${checkOutYYYYMMDD}&adults=${adults}&children=${children}`,
        icon: '🏠',
        types: ['airbnb', 'homestay']
      },
      {
        label: 'VRBO',
        url: `https://www.vrbo.com/search?destination=${encodeURIComponent(destination)}&startDate=${checkInYYYYMMDD}&endDate=${checkOutYYYYMMDD}&adults=${adults}&children=${children}`,
        icon: '🏡',
        types: ['airbnb', 'homestay']
      },
      {
        label: 'Booking.com Apartments',
        url: `https://www.booking.com/searchresults.html?ss=${encodedSearch}&checkin=${checkInYYYYMMDD}&checkout=${checkOutYYYYMMDD}&group_adults=${adults}&group_children=${children}&no_rooms=${rooms}&nflt=ht_id%3D201&selected_currency=INR`,
        icon: '🏘️',
        types: ['airbnb', 'homestay']
      }
    ];

    const hostelOptions = [
      {
        label: 'Hostelworld',
        url: `https://www.hostelworld.com/search?search_keywords=${encodeURIComponent(destination)}&DateIn=${checkInYYYYMMDD}&DateOut=${checkOutYYYYMMDD}&guests=${adults}`,
        icon: '🎒',
        types: ['hostel']
      },
      {
        label: 'Hostels.com',
        url: `https://www.hostels.com/search?q=${encodeURIComponent(destination)}&date_from=${checkInYYYYMMDD}&date_to=${checkOutYYYYMMDD}&guests=${adults}`,
        icon: '🛏️',
        types: ['hostel']
      }
    ];

    const reviewOptions = [
      {
        label: 'TripAdvisor Reviews',
        url: `https://www.tripadvisor.com/Search?q=${encodedSearch}`,
        icon: '⭐',
        types: ['hotels', 'airbnb', 'homestay', 'hostel']
      }
    ];

    const allOptions = [...baseOptions, ...hotelOptions, ...airbnbOptions, ...hostelOptions, ...reviewOptions];
    return allOptions.filter(opt => opt.types.includes(normalizedAccommodationType));
  };

  const options = getBookingOptions();

  if (options.length === 0) {
    console.warn('No booking options for accommodation type:', accommodationType, '-> normalized:', normalizedAccommodationType);
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-sm font-medium hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-all cursor-pointer"
      >
        <span>Book</span>
        <span className="text-xs">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/10"
            style={{ zIndex: 99998 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={dropdownRef}
            className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 py-2 min-w-[260px] max-h-[70vh] overflow-y-auto"
            style={{
              zIndex: 99999,
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            <div className="px-4 py-2 border-b border-gray-100 mb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase">
                {normalizedAccommodationType === 'airbnb' ? 'Book This Property' :
                 normalizedAccommodationType === 'homestay' ? 'Book This Homestay' :
                 normalizedAccommodationType === 'hostel' ? 'Book This Hostel' : 'Book This Hotel'}
              </p>
              <p className="text-sm text-gray-700 truncate font-medium">{hotel.name}</p>
              {hotel.price && (
                <p className="text-xs text-teal-600 font-semibold">
                  {typeof hotel.price === 'number' ? formatCurrency(hotel.price, hotel.currency || 'INR') : hotel.price}
                  {hotel.pricePerNight && <span className="text-gray-400"> /night</span>}
                </p>
              )}
            </div>
            {options.map((option, idx) => (
              <a
                key={idx}
                href={option.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <span className="w-5 text-center text-base">{option.icon}</span>
                <span className="whitespace-nowrap">{option.label}</span>
              </a>
            ))}
          </div>
        </>
      )}
    </>
  );
};

import { formatCurrency } from "../../utils/currencyFormatter";

const HotelInformationSection = ({ accommodationDetails, tripData, bookingLinks, tripSummary, dayWiseItinerary, isExpanded = false, onToggleExpand, onHotelsLoaded }) => {
  const [hotelsByCity, setHotelsByCity] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState({});
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const accommodationTypes = getAccommodationTypes(tripData?.accommodation);
  const sectionTitle = getSectionTitle(accommodationTypes);

  const getPrimaryAccommodationType = () => {
    if (accommodationTypes.includes('hotels')) return 'hotels';
    if (accommodationTypes.includes('airbnb')) return 'airbnb';
    if (accommodationTypes.includes('homestay')) return 'homestay';
    if (accommodationTypes.includes('hostel')) return 'hostel';
    return 'hotels';
  };

  const primaryAccommodationType = getPrimaryAccommodationType();

  const getStarRating = () => {
    if (accommodationDetails?.[0]?.hotelCategory) {
      const match = accommodationDetails[0].hotelCategory.match(/(\d+)/);
      return match ? parseInt(match[1]) : 3;
    }
    if (tripData?.accommodation?.length > 0) {
      const accStr = tripData.accommodation.join(' ').toLowerCase();
      if (accStr.includes('5')) return 5;
      if (accStr.includes('4')) return 4;
      if (accStr.includes('3')) return 3;
      if (accStr.includes('2')) return 2;
    }
    return 3;
  };

  const getCitiesFromItinerary = () => {
    const cities = [];
    const startDate = tripData?.startDate ? new Date(tripData.startDate) : null;

    if (tripSummary?.citiesBreakdown && tripSummary.citiesBreakdown.length > 0) {
      let currentDayIndex = 0;
      tripSummary.citiesBreakdown.filter(c => c.type !== "departure").forEach((cityInfo) => {
        const nights = cityInfo.nights !== undefined ? parseInt(cityInfo.nights) : 1;
        
        if (nights > 0) {
          const checkInDate = new Date(startDate);
          checkInDate.setDate(startDate.getDate() + currentDayIndex);

          const checkOutDate = new Date(checkInDate);
          checkOutDate.setDate(checkInDate.getDate() + nights);

          cities.push({
            city: cityInfo.city,
            nights: nights,
            checkIn: checkInDate.toISOString().split('T')[0],
            checkOut: checkOutDate.toISOString().split('T')[0],
            days: cityInfo.days || `Day ${currentDayIndex + 1}-${currentDayIndex + nights}`
          });
        }

        currentDayIndex += nights;
      });
      return cities;
    }

    if (dayWiseItinerary && dayWiseItinerary.length > 0) {
      let currentCity = null;
      let cityStartDay = 0;
      let nightsCount = 0;

      dayWiseItinerary.forEach((day, index) => {
        const title = day.title || '';
        let cityMatch = title.match(/(?:in|to|at|exploring|arrival|departure)\s+([A-Z][a-zA-Z\s]+)/i);
        let extractedCity = cityMatch ? cityMatch[1].trim() : null;

        if (!extractedCity && day.location) {
          extractedCity = day.location;
        }

        if (extractedCity && extractedCity !== currentCity) {
          if (currentCity && nightsCount > 0) {
            const checkInDate = new Date(startDate);
            checkInDate.setDate(startDate.getDate() + cityStartDay);
            const checkOutDate = new Date(checkInDate);
            checkOutDate.setDate(checkInDate.getDate() + nightsCount);

            cities.push({
              city: currentCity,
              nights: nightsCount,
              checkIn: checkInDate.toISOString().split('T')[0],
              checkOut: checkOutDate.toISOString().split('T')[0],
              days: `Day ${cityStartDay + 1}-${cityStartDay + nightsCount}`
            });
          }
          currentCity = extractedCity;
          cityStartDay = index;
          nightsCount = 1;
        } else if (currentCity) {
          nightsCount++;
        } else {
          currentCity = extractedCity || tripData?.destination?.split(',')[0]?.trim();
          cityStartDay = index;
          nightsCount = 1;
        }
      });

      if (currentCity && nightsCount > 0) {
        const checkInDate = new Date(startDate);
        checkInDate.setDate(startDate.getDate() + cityStartDay);
        const checkOutDate = new Date(checkInDate);
        checkOutDate.setDate(checkInDate.getDate() + nightsCount);

        cities.push({
          city: currentCity,
          nights: nightsCount,
          checkIn: checkInDate.toISOString().split('T')[0],
          checkOut: checkOutDate.toISOString().split('T')[0],
          days: `Day ${cityStartDay + 1}-${cityStartDay + nightsCount}`
        });
      }
    }

    if (cities.length === 0 && tripData?.destination) {
      cities.push({
        city: tripData.destination.split(',')[0].trim(),
        nights: tripData?.endDate && tripData?.startDate
          ? Math.ceil((new Date(tripData.endDate) - new Date(tripData.startDate)) / (1000 * 60 * 60 * 24))
          : 1,
        checkIn: tripData.startDate,
        checkOut: tripData.endDate,
        days: 'All Days'
      });
    }

    return cities;
  };

  const citiesInItinerary = getCitiesFromItinerary();

  useEffect(() => {
    const fetchHotelsForAllCities = async () => {
      if (hasFetched || citiesInItinerary.length === 0) return;

      setIsLoading(true);
      setError(null);

      const starRating = getStarRating();
      const totalAdults = tripData?.adults || 1;
      const totalChildren = tripData?.children || 0;
      const totalInfants = tripData?.infants || 0;
      const rooms = Math.max(1, Math.ceil(totalAdults / 2));

      const newHotelsByCity = {};
      const newLoadingCities = {};

      for (const cityInfo of citiesInItinerary) {
        newLoadingCities[cityInfo.city] = true;
        setLoadingCities({ ...newLoadingCities });

        try {
          const result = await searchHotels({
            location: cityInfo.city,
            checkIn: cityInfo.checkIn,
            checkOut: cityInfo.checkOut,
            adults: totalAdults,
            children: totalChildren,
            infants: totalInfants,
            starRating,
            rooms,
            limit: 3,
            accommodation: tripData?.accommodation || []
          });

          if (result.success && result.hotels.length > 0) {
            newHotelsByCity[cityInfo.city] = {
              hotels: result.hotels,
              cityInfo: cityInfo
            };
          }
        } catch (err) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "search");
            scope.setTag("app", "web");
            scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
            Sentry.captureException(new SearchError(err instanceof Error ? err.message : String(err)));
          });
          console.error(`Failed to fetch hotels for ${cityInfo.city}:`, err);
        }

        newLoadingCities[cityInfo.city] = false;
        setLoadingCities({ ...newLoadingCities });
      }

      setHotelsByCity(newHotelsByCity);
      if (onHotelsLoaded) {
        onHotelsLoaded(newHotelsByCity);
      }
      setHasFetched(true);
      setIsLoading(false);
    };

    fetchHotelsForAllCities();
  }, [tripData?.destination, tripData?.startDate, hasFetched, citiesInItinerary.length]);

  const parseNightsToDate = (nightsStr) => {
    if (!tripData?.startDate || !nightsStr) return { checkIn: '-', checkOut: '-' };

    const startDate = new Date(tripData.startDate);
    const numbers = nightsStr.match(/\d+/g);
    if (!numbers || numbers.length === 0) return { checkIn: '-', checkOut: '-' };

    const firstNight = parseInt(numbers[0]);
    const lastNight = parseInt(numbers[numbers.length - 1]);

    const checkInDate = new Date(startDate);
    checkInDate.setDate(startDate.getDate() + firstNight - 1);

    const checkOutDate = new Date(startDate);
    checkOutDate.setDate(startDate.getDate() + lastNight);

    const formatDate = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return {
      checkIn: formatDate(checkInDate),
      checkOut: formatDate(checkOutDate),
    };
  };

  const formatTripDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const starRating = getStarRating();
  const totalGuests = (tripData?.adults || 1) + (tripData?.children || 0);
  const guestsText = `${totalGuests} Pax (${tripData?.adults || 1} Adults${tripData?.children ? `, ${tripData.children} Children` : ''})`;

  const hasHotels = Object.keys(hotelsByCity).length > 0;
  const showingRealHotels = hasHotels;

  const getAccommodationLabel = () => {
    if (accommodationTypes.length > 1) return 'stays';
    switch (primaryAccommodationType) {
      case 'airbnb': return 'Airbnb properties';
      case 'homestay': return 'homestays';
      case 'hostel': return 'hostels';
      default: return `${starRating}-star hotels`;
    }
  };

  if (isLoading && !hasHotels) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 lg:p-8 mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 flex items-center gap-2 sm:gap-3">
          <div className="w-1.5 h-8 bg-teal-600 rounded-full flex-shrink-0"></div>
          <span className="whitespace-nowrap text-left">{sectionTitle}</span>
        </h2>
        <div className="space-y-4">
          {citiesInItinerary.map((cityInfo, idx) => (
            <div key={idx} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600"></div>
                <span className="text-gray-600">Searching for {getAccommodationLabel()} in {cityInfo.city}...</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!hasHotels && !isLoading) {
    return null;
  }

  const getAccommodationBadges = () => {
    const badges = [];
    if (accommodationTypes.includes('hotels')) {
      badges.push({ label: `${starRating} Star Hotels`, icon: '🏨' });
    }
    if (accommodationTypes.includes('airbnb')) {
      badges.push({ label: 'Airbnb', icon: '🏠' });
    }
    if (accommodationTypes.includes('homestay')) {
      badges.push({ label: 'Homestay', icon: '💕' });
    }
    if (accommodationTypes.includes('hostel')) {
      badges.push({ label: 'Hostel', icon: '🎒' });
    }
    return badges;
  };

  const getTypeDisplay = (hotel) => {
    if (hotel.typeLabel && hotel.typeIcon) {
      return { label: hotel.typeLabel, icon: hotel.typeIcon };
    }
    if (hotel.starRating) {
      return { label: `${hotel.starRating} Star`, icon: '⭐' };
    }
    return { label: 'Hotel', icon: '🏨' };
  };

  const renderAirbnbCard = (hotel, cityInfo, hotelIndex) => {
    return (
      <div key={hotelIndex} className="border border-pink-200 rounded-xl p-4 bg-gradient-to-r from-pink-50 to-red-50">
        <div className="flex gap-3 mb-3">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-red-500 rounded-lg flex items-center justify-center text-white text-2xl flex-shrink-0">
            🏠
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900 text-sm">Search on Airbnb</p>
            <p className="text-xs text-gray-500">Find unique stays and experiences</p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-100 text-pink-700 rounded text-xs font-medium mt-1">
              🏠 Airbnb
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-gray-500">Check In</p>
            <p className="font-medium text-teal-700">{formatTripDate(cityInfo.checkIn)}</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2">
            <p className="text-gray-500">Check Out</p>
            <p className="font-medium text-orange-700">{formatTripDate(cityInfo.checkOut)}</p>
          </div>
        </div>

        <a
          href={hotel.searchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg text-sm font-medium hover:from-pink-600 hover:to-red-600 transition-all"
        >
          Search on Airbnb
          <ExternalLinkIcon />
        </a>
      </div>
    );
  };

  const renderMobileHotelCard = (hotel, cityInfo, hotelIndex) => {
    if (hotel.isAirbnbPlaceholder) {
      return renderAirbnbCard(hotel, cityInfo, hotelIndex);
    }

    const typeDisplay = getTypeDisplay(hotel);
    const hotelStars = hotel.starRating || starRating;

    return (
      <div key={hotelIndex} className="border border-gray-200 rounded-xl p-4">
        <div className="flex gap-3 mb-3">
          {hotel.photo && (
            <img
              src={hotel.photo}
              alt={hotel.name}
              className="w-20 h-16 object-cover rounded-lg flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm truncate">{hotel.name || hotel.hotelName}</p>
            <p className="text-xs text-gray-500 truncate">{hotel.address || hotel.location}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {hotel.type === 'hotel' && hotel.starRating ? (
                <div className="flex items-center gap-0.5">
                  {[...Array(hotel.starRating)].map((_, i) => <StarIcon key={i} />)}
                  <span className="text-xs text-gray-600 ml-1">{hotel.starRating} Star</span>
                </div>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                  {typeDisplay.icon} {typeDisplay.label}
                </span>
              )}
              {hotel.rating && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">
                  ⭐ {hotel.rating.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
          <div className="bg-gray-50 rounded-lg p-2">
            <p className="text-gray-500">Check In</p>
            <p className="font-medium text-teal-700">{formatTripDate(cityInfo.checkIn)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 text-right">
            <p className="text-gray-500">Check Out</p>
            <p className="font-medium text-orange-700">{formatTripDate(cityInfo.checkOut)}</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{guestsText}</span>
          <AccommodationBookingDropdown
            hotel={hotel}
            checkIn={formatTripDate(cityInfo.checkIn)}
            checkOut={formatTripDate(cityInfo.checkOut)}
            guests={guestsText}
            starRating={hotelStars}
            tripData={tripData}
            accommodationType={primaryAccommodationType}
          />
        </div>
      </div>
    );
  };

  const renderAirbnbRow = (hotel, cityInfo, hotelIndex) => {
    return (
      <tr key={hotelIndex} className="border-b border-gray-100 bg-gradient-to-r from-pink-50 to-red-50">
        <td className="py-4 px-4" colSpan="2">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-red-500 rounded-lg flex items-center justify-center text-white text-2xl">
              🏠
            </div>
            <div>
              <p className="font-semibold text-gray-900">Search on Airbnb</p>
              <p className="text-xs text-gray-500">Find unique stays and experiences</p>
            </div>
          </div>
        </td>
        <td className="py-4 px-4 text-center">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-100 text-pink-700 rounded-lg text-sm font-medium">
            🏠 Airbnb
          </span>
        </td>
        <td className="py-4 px-4 text-center">
          <span className="inline-block px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium whitespace-nowrap">
            {formatTripDate(cityInfo.checkIn)}
          </span>
        </td>
        <td className="py-4 px-4 text-center">
          <span className="inline-block px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-sm font-medium whitespace-nowrap">
            {formatTripDate(cityInfo.checkOut)}
          </span>
        </td>
        <td className="py-4 px-4 text-center">
          <span className="text-sm text-gray-700">{tripData?.adults || 2} Adults</span>
        </td>
        <td className="py-4 px-4 text-center">
          <a
            href={hotel.searchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-lg text-sm font-medium hover:from-pink-600 hover:to-red-600 transition-all"
          >
            Search
            <ExternalLinkIcon />
          </a>
        </td>
      </tr>
    );
  };

  const renderDesktopHotelRow = (hotel, cityInfo, hotelIndex) => {
    if (hotel.isAirbnbPlaceholder) {
      return renderAirbnbRow(hotel, cityInfo, hotelIndex);
    }

    const typeDisplay = getTypeDisplay(hotel);
    const hotelStars = hotel.starRating || starRating;

    return (
      <tr key={hotelIndex} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="py-4 px-4">
          <div className="flex items-center gap-3">
            {hotel.photo && (
              <img
                src={hotel.photo}
                alt={hotel.name}
                className="w-14 h-14 object-cover rounded-lg flex-shrink-0 border border-gray-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{hotel.name || hotel.hotelName}</p>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{hotel.address || hotel.location}</p>
              {hotel.reviewScore && (
                <p className="text-xs text-teal-600 mt-0.5">
                  {hotel.reviewScore}/10 {hotel.reviewWord && `• ${hotel.reviewWord}`}
                </p>
              )}
            </div>
          </div>
        </td>
        <td className="py-4 px-4 text-center">
          <div className="flex flex-col items-center gap-1">
            {hotel.type === 'hotel' && hotel.starRating ? (
              <>
                <div className="flex items-center gap-0.5">
                  {[...Array(hotel.starRating)].map((_, i) => <StarIcon key={i} />)}
                </div>
                <span className="text-xs text-gray-600 font-medium">{hotel.starRating} Star</span>
              </>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">
                {typeDisplay.icon} {typeDisplay.label}
              </span>
            )}
          </div>
        </td>
        <td className="py-4 px-4 text-center">
          {hotel.rating ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-sm font-semibold">
              ⭐ {hotel.rating.toFixed(1)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">N/A</span>
          )}
        </td>
        <td className="py-4 px-4 text-center">
          <span className="inline-block px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium whitespace-nowrap">
            {formatTripDate(cityInfo.checkIn)}
          </span>
        </td>
        <td className="py-4 px-4 text-center">
          <span className="inline-block px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-sm font-medium whitespace-nowrap">
            {formatTripDate(cityInfo.checkOut)}
          </span>
        </td>
        <td className="py-4 px-4 text-center">
          <span className="text-sm text-gray-700">{tripData?.adults || 2} Adults</span>
          {tripData?.children > 0 && (
            <span className="block text-xs text-gray-500">{tripData.children} Children</span>
          )}
        </td>
        <td className="py-4 px-4 text-center">
          <AccommodationBookingDropdown
            hotel={hotel}
            checkIn={formatTripDate(cityInfo.checkIn)}
            checkOut={formatTripDate(cityInfo.checkOut)}
            guests={guestsText}
            starRating={hotelStars}
            tripData={tripData}
            accommodationType={hotel.type || primaryAccommodationType}
          />
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 lg:p-8 mb-6">
      <div
        onClick={(e) => {
          if (window.innerWidth >= 1024) {
            onToggleExpand(e);
          }
        }}
        className="w-full flex items-center justify-between mb-0 cursor-default lg:cursor-pointer group"
      >
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
          <div className="w-1.5 h-8 bg-teal-600 rounded-full flex-shrink-0"></div>
          <span className="whitespace-nowrap text-left">{sectionTitle}</span>
        </h2>
        <div className="flex items-center gap-3">
          {showingRealHotels && (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap flex-shrink-0">
              Live Results
            </span>
          )}
          {isLoading && (
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-2">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
              Searching...
            </span>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 hidden lg:block ${isExpanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <div className={`mt-6 ${!isExpanded ? 'block lg:hidden' : 'block'}`}>

      <div className="bg-gray-50 rounded-lg p-3 md:p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3 md:gap-6 text-xs md:text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Trip:</span>
            <span className="font-medium text-gray-900">
              {tripSummary?.citiesBreakdown?.length
                ? tripSummary.citiesBreakdown.map(c => c.city).join(' → ')
                : tripData?.destination}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Type:</span>
            {getAccommodationBadges().map((badge, idx) => (
              <span key={idx} className="font-medium text-gray-900 flex items-center gap-1 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                <span>{badge.icon}</span>
                <span>{badge.label}</span>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Guests:</span>
            <span className="font-medium text-gray-900">{guestsText}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-700">
          Note: {error}. Showing available results.
        </div>
      )}

      <div className="space-y-8">
        {citiesInItinerary.map((cityInfo, cityIndex) => {
          const cityData = hotelsByCity[cityInfo.city];
          const hotels = cityData?.hotels || [];
          const isLoadingCity = loadingCities[cityInfo.city];

          return (
            <div key={cityIndex} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📍</span>
                    <div>
                      <h3 className="font-bold text-gray-900">{cityInfo.city}</h3>
                      <p className="text-xs text-gray-500">
                        {cityInfo.nights} {cityInfo.nights === 1 ? 'Night' : 'Nights'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full font-medium">
                      {formatTripDate(cityInfo.checkIn)}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                      {formatTripDate(cityInfo.checkOut)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4">
                {isLoadingCity ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                    <span className="ml-3 text-gray-500 text-sm">Searching hotels in {cityInfo.city}...</span>
                  </div>
                ) : hotels.length > 0 ? (
                  <>
                    <div className="md:hidden space-y-4">
                      {hotels.map((hotel, hotelIndex) => renderMobileHotelCard(hotel, cityInfo, hotelIndex))}
                    </div>

                    <div className="hidden md:block overflow-x-auto" style={{ overflow: 'visible' }}>
                      <table className="w-full table-fixed">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 w-[32%]">Hotel Name</th>
                            <th className="text-center py-3 px-3 text-xs font-semibold text-gray-600 w-[12%]">Type</th>
                            <th className="text-center py-3 px-3 text-xs font-semibold text-gray-600 w-[10%]">Rating</th>
                            <th className="text-center py-3 px-3 text-xs font-semibold text-gray-600 w-[12%]">Check In</th>
                            <th className="text-center py-3 px-3 text-xs font-semibold text-gray-600 w-[12%]">Check Out</th>
                            <th className="text-center py-3 px-3 text-xs font-semibold text-gray-600 w-[10%]">Guests</th>
                            <th className="text-center py-3 px-3 text-xs font-semibold text-gray-600 w-[12%]">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hotels.map((hotel, hotelIndex) => renderDesktopHotelRow(hotel, cityInfo, hotelIndex))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    No hotels found for {cityInfo.city}. Try searching on booking platforms directly.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showingRealHotels && (
        <p className="text-xs text-gray-400 mt-6 text-right">
          Hotel data from external booking APIs. Prices and availability may vary.
        </p>
      )}
      </div>
    </div>
  );
};

export default HotelInformationSection;
